<template>
  <v-container id="contents" fluid>
    <v-card>
      <v-system-bar color="brown" />
      <v-toolbar flat>
        <v-icon large left color="brown">mdi-cloud-upload</v-icon>
        <v-toolbar-title>
          アップロード
        </v-toolbar-title>
        <v-spacer />
        <v-file-input
          :rules="[v => !v || v.size < 1e8 || '100MB以下です']"
          v-model="file"
          accept="image/*,video/*"
          placeholder="ファイル選択（100MB以下）"
          clearable
          dense
          hide-details
          outlined
          small-chips
        >
        </v-file-input>
        <v-spacer />
        <v-btn
          :disabled="file == null"
          :loading="uploading"
          @click="onUpload"
          color="brown"
        >
          <v-icon left>mdi-light mdi-upload</v-icon>
          アップロード
          <template v-slot:loader>
            <v-progress-circular v-model="progress" :value="progress" />
          </template>
        </v-btn>
      </v-toolbar>
    </v-card>
    <v-card>
      <v-system-bar color="content" />
      <v-skeleton-loader
        :loading="contentsLoading"
        type="actions"
        transition="fade-transition"
      >
        <v-toolbar flat>
          <v-icon large left color="content">mdi-play-circle</v-icon>
          <v-toolbar-title>
            コンテンツ一覧
          </v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="contentSearch"
            prepend-icon="mdi-magnify"
            label="検索"
            single-line
            hide-details
            clearable
            outlined
            dense
          />
          <v-spacer />
        </v-toolbar>
      </v-skeleton-loader>
      <v-dialog
        persistent
        max-width="600px"
        v-model="deleteDialog"
        @keydown.esc="deleteDialog = false"
      >
        <confirm-delete-card
          :subtitle="itemToDelete.title"
          @cancel="deleteDialog = false"
          @confirm="removeContent(itemToDelete)"
        />
      </v-dialog>
      <v-data-table
        :headers="headers"
        :items="contents"
        :loading="contentsLoading"
        :search="contentSearch"
        loading-text="ロード中"
        no-data-text="データがありません"
        class="pa-4"
      >
        <template v-slot:item.url="{ item }">
          <v-skeleton-loader
            :loading="contentsLoading"
            type="card"
            transition="scale-transition"
          >
            <content-card :content="item" :ref="item.id" />
          </v-skeleton-loader>
        </template>
        <template v-slot:item.title="{ item }">
          <v-edit-dialog
            large
            :return-value.sync="item.title"
            @save="saveContent(item)"
          >
            <v-btn small icon left><v-icon>mdi-playlist-edit</v-icon></v-btn>
            <span>{{ item.title }}</span>
            <template v-slot:input>
              <v-text-field
                :rules="[v => v.length >= 2 || '2文字以上']"
                v-model="item.title"
                single-line
                autofocus
              />
            </template>
          </v-edit-dialog>
        </template>
        <template v-slot:item.duration="{ item }">
          {{ formatContentDuration(item.duration || 0) }}
        </template>
        <template v-slot:item.created="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                {{ getContentCreated(item) }}
              </span>
            </template>
            <span> {{ getContentCreatedFull(item) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.playlistCount="{ item }">
          {{ countInPlaylists(item) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-chip
            label
            @click="confirmDelete(item)"
            dark
            color="error"
            class="mr-2"
          >
            <v-icon left>mdi-delete</v-icon>削除</v-chip
          >
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { fmt, fmtToNow } from '@/plugins/datefns'
import { mapActions, mapGetters } from 'vuex'
import { flatten } from 'lodash/core'
import ContentCard from '@/components/content/ContentCard'
import ConfirmDeleteCard from '@/components/dialogs/ConfirmDeleteCard'
import firebase from 'firebase'
import {
  addContent,
  updateContent,
  deleteContent
} from '@/firebaseAccess/firestoreAccess'

export default {
  name: 'DashboardContents',
  components: {
    ConfirmDeleteCard,
    ContentCard
  },
  data: () => ({
    fmt: fmt,
    contentsLoading: true,
    deleteDialog: false,
    snackbarSave: false,
    itemToDelete: {},
    contentSearch: '',
    headers: [
      { text: 'コンテンツ', value: 'url', sortable: false, filterable: false },
      { text: 'コンテンツ名', value: 'title' },
      { text: '長さ', value: 'duration' },
      { text: '追加日', value: 'created' },
      { text: '予約プレイリスト', value: 'playlistCount' },
      {
        text: '',
        value: 'actions',
        align: 'end',
        sortable: false,
        filterable: false
      }
    ],

    // file upload
    file: null,
    progress: 0,
    uploading: false,
    finished: false
  }),
  computed: {
    ...mapGetters({
      userId: 'users/getActiveUserId',
      contents: 'contents/getContents',
      playlists: 'playlists/getPlaylists'
    }),
    playlistContents() {
      return flatten(this.playlists.map(p => p.contents))
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack',

      bindContentsRef: 'contents/bindRef',
      bindPlaylistsRef: 'playlists/bindRef'
    }),
    formatContentDuration(sec) {
      const seconds = ('0' + parseInt(sec % 60)).slice(-2)
      const minutes = ('0' + parseInt(sec / 60)).slice(-2)
      return minutes + ':' + seconds
    },
    getContentCreated(content) {
      if (content.created !== undefined) {
        return fmtToNow(content.created.toDate())
      }
      return '--'
    },
    getContentCreatedFull(content) {
      if (content.created !== undefined) {
        return fmt(content.created.toDate())
      }
      return '--'
    },
    countInPlaylists(item) {
      if (this.playlists.length === 0) return 0
      return this.playlistContents.filter(c => c.uid === item.id).length
    },
    confirmDelete(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },
    removeContent(content) {
      deleteContent(content.id).then(() => {
        this.itemToDelete = {}
        this.deleteDialog = false
        this.showSnack({ message: '削除されました' })
      })
    },
    saveContent(content) {
      updateContent(content)
        .then(() => {
          this.showSnack({ message: '保存されました' })
        })
        .catch(err => {
          console.error(err)
        })
    },
    async onUpload() {
      this.uploading = true
      const now = firebase.firestore.Timestamp.now()
      const extension = this.file.name.split('.').pop()

      const newContent = {
        title: this.file.name,
        type: this.file.type,
        owner: this.userId,
        created: now,
        updated: now
      }
      const contentRef = await addContent(newContent)

      const storageRef = firebase
        .storage()
        .ref()
        .child(`contents/${this.userId}/${contentRef.id}.${extension}`)
        .put(this.file)

      storageRef.on(
        'state_changed',
        snapshot => {
          this.progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        },
        error => {
          this.showSnack({
            message: error,
            color: 'error'
          })
        },
        () => {
          this.showSnack({
            message: '内容が投稿されました！少々お待ちください...'
          })
          this.progress = 0
          this.uploading = false
          this.finished = true
          this.file = null
          setTimeout(
            function() {
              this.finished = false
            }.bind(this),
            6000
          )
        }
      )
    }
  },
  created: function() {
    const contents = this.bindContentsRef()
    const playlists = this.bindPlaylistsRef()

    Promise.all([contents, playlists]).then(() => {
      this.contentsLoading = false
    })
  }
}
</script>

<style scoped></style>
